import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaClipboardList, FaTrash, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import orderService, { computeTotals } from '../utils/orderService';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    orderService.getById(id).then(setOrder).catch(() => setOrder(null));
  }, [id]);

  if (!order) {
    return (
      <section className="col-span-1 md:col-span-10 p-4 md:py-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">{t('dashboard.orders.detailNotFound')}</h2>
          <p className="mt-2 text-sm text-slate-500">{t('dashboard.orders.detailNotFoundNote')}</p>
          <button
            onClick={() => navigate('/dashboard/orders')}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            <FaArrowLeft className="h-4 w-4" /> {t('dashboard.orders.backToOrders')}
          </button>
        </div>
      </section>
    );
  }

  const totals = computeTotals(order);
  const createdAt = order.createdAt ? new Date(order.createdAt) : null;

  const handleDelete = () => {
    if (window.confirm(`Delete order #${order.id}?`)) {
      orderService.remove(order.id);
      navigate('/dashboard/orders');
    }
  };

  return (
    <section className="col-span-1 md:col-span-10 p-4 md:py-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 shadow-sm md:p-6">
        <div className="mb-6 flex flex-col justify-between gap-4 rounded-lg bg-white p-5 text-left shadow-sm md:flex-row md:items-center">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
              {t('dashboard.orders.order')}
            </p>
            <h2 className="mt-2 truncate text-3xl font-semibold text-[#00595A]">
              {t('dashboard.orders.detailTitle', { id: order.id })}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {createdAt ? createdAt.toLocaleString() : t('dashboard.orders.createdAtFallback')}
            </p>
          </div>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-[#00595A] text-white shadow-sm">
            <FaClipboardList className="h-6 w-6" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2 md:p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <FaUser className="h-4 w-4 text-slate-500" />
              {t('dashboard.orders.customerSection')}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.common.name')}</p>
                <p className="mt-2 text-base font-semibold text-slate-900">{order.customer_name}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.phone')}</p>
                <p className="mt-2 text-base font-semibold text-slate-900">{order.phone || '-'}</p>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-semibold text-slate-800">{t('dashboard.orders.itemsSection')}</p>
              <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
                <div className="hidden grid-cols-12 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 sm:grid">
                  <div className="col-span-6">{t('dashboard.products.detailLabel')}</div>
                  <div className="col-span-2 text-right">{t('dashboard.orders.formQuantity')}</div>
                  <div className="col-span-2 text-right">{t('dashboard.orders.formPrice')}</div>
                  <div className="col-span-2 text-right">{t('dashboard.orders.total')}</div>
                </div>
                <div className="divide-y divide-slate-100 bg-white">
                  {(order.items || []).map((it, idx) => {
                    const lineTotal = (Number(it.quantity) || 0) * (Number(it.price) || 0);
                    return (
                      <div key={idx} className="px-4 py-4 sm:grid sm:grid-cols-12 sm:items-center">
                        <div className="sm:col-span-6">
                          <p className="text-sm font-semibold text-slate-900">{it.name}</p>
                        </div>
                        <div className="mt-2 flex justify-between gap-3 text-sm sm:col-span-2 sm:mt-0 sm:justify-end">
                          <span className="text-slate-500 sm:hidden">{t('dashboard.orders.formQuantity')}</span>
                          <span className="font-semibold text-slate-700">{it.quantity}</span>
                        </div>
                        <div className="mt-2 flex justify-between gap-3 text-sm sm:col-span-2 sm:mt-0 sm:justify-end">
                          <span className="text-slate-500 sm:hidden">{t('dashboard.orders.formPrice')}</span>
                          <span className="text-slate-700">${Number(it.price).toFixed(2)}</span>
                        </div>
                        <div className="mt-2 flex justify-between gap-3 text-sm sm:col-span-2 sm:mt-0 sm:justify-end">
                          <span className="text-slate-500 sm:hidden">{t('dashboard.orders.total')}</span>
                          <span className="font-semibold text-slate-900">${lineTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm md:p-6">
            <h3 className="text-lg font-semibold text-slate-900">{t('dashboard.orders.summarySection')}</h3>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t('dashboard.orders.subtotal')}</span>
                <span className="font-semibold text-slate-900">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t('dashboard.orders.discount')}</span>
                <span className="font-semibold text-slate-900">${totals.discount.toFixed(2)}</span>
              </div>
              <div className="h-px bg-slate-200" />
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t('dashboard.orders.total')}</span>
                <span className="text-lg font-semibold text-slate-900">${totals.total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => navigate('/dashboard/orders')}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
              >
                <FaArrowLeft className="h-4 w-4" /> {t('dashboard.orders.backToOrders')}
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-rose-100 px-4 py-3 text-sm font-semibold text-rose-800 transition hover:bg-rose-200"
              >
                <FaTrash className="h-4 w-4" /> {t('dashboard.orders.deleteOrder')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderDetail;
